import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'
import type { Take } from '../api/types'
import { takeUrl } from '../api/client'
import { formatDateTime, formatDb, formatMs } from '../lib/format'

export type TakeListProps = {
  takes: Take[]
  disabled?: boolean
  onSetActive: (takeId: string) => void
  onPatch: (takeId: string, patch: { gain?: number; offset?: number }) => void
  onDelete: (takeId: string) => void
}

const OFFSET_RANGE = 0.5 // ±500 мс вокруг записанного значения

type TakeRowProps = {
  take: Take
  index: number
  total: number
  disabled: boolean
  baseOffset: number
  onSetActive: TakeListProps['onSetActive']
  onPatch: TakeListProps['onPatch']
  onDelete: TakeListProps['onDelete']
}

function TakeRow({
  take,
  index,
  total,
  disabled,
  baseOffset,
  onSetActive,
  onPatch,
  onDelete,
}: TakeRowProps): JSX.Element {
  const [offset, setOffset] = useState(take.offset)
  const [gain, setGain] = useState(take.gain)

  useEffect(() => setOffset(take.offset), [take.offset])
  useEffect(() => setGain(take.gain), [take.gain])

  const commitOffset = (): void => {
    if (Math.abs(offset - take.offset) < 0.0005) return
    onPatch(take.id, { offset: Number(offset.toFixed(3)) })
  }
  const commitGain = (): void => {
    if (Math.abs(gain - take.gain) < 0.05) return
    onPatch(take.id, { gain: Number(gain.toFixed(1)) })
  }

  return (
    <div className={`take-row${take.isActive ? ' active' : ''}`}>
      <div className="row wrap">
        <label className="row" style={{ gap: 5, cursor: 'pointer' }}>
          <input
            type="radio"
            name={`take-${take.lineId}`}
            checked={take.isActive}
            disabled={disabled}
            onChange={() => onSetActive(take.id)}
          />
          <span style={{ color: 'var(--text)' }}>Дубль {total - index}</span>
        </label>
        <span className="faint mono">{take.duration.toFixed(2)} с</span>
        <span className="faint">{formatDateTime(take.createdAt)}</span>
        {take.author && take.author !== 'local' ? (
          <span className="badge">{take.author}</span>
        ) : null}
        <span className="spacer" />
        <button
          type="button"
          className="danger small"
          disabled={disabled}
          onClick={() => {
            if (window.confirm('Удалить дубль?')) onDelete(take.id)
          }}
        >
          Удалить
        </button>
      </div>

      <audio src={takeUrl(take.id)} controls preload="none" />

      <div className="take-controls">
        <div>
          <label htmlFor={`offset-${take.id}`}>
            Сдвиг: <span className="mono">{formatMs(offset - baseOffset)}</span>
          </label>
          <input
            id={`offset-${take.id}`}
            type="range"
            min={baseOffset - OFFSET_RANGE}
            max={baseOffset + OFFSET_RANGE}
            step={0.005}
            value={offset}
            disabled={disabled}
            onChange={(e) => setOffset(Number(e.currentTarget.value))}
            onPointerUp={commitOffset}
            onKeyUp={commitOffset}
            onBlur={commitOffset}
          />
        </div>
        <div>
          <label htmlFor={`gain-${take.id}`}>
            Громкость: <span className="mono">{formatDb(gain)}</span>
          </label>
          <input
            id={`gain-${take.id}`}
            type="range"
            min={-20}
            max={20}
            step={0.5}
            value={gain}
            disabled={disabled}
            onChange={(e) => setGain(Number(e.currentTarget.value))}
            onPointerUp={commitGain}
            onKeyUp={commitGain}
            onBlur={commitGain}
          />
        </div>
      </div>
      <div className="row">
        <span className="faint">
          Абсолютная поправка: <span className="mono">{formatMs(offset)}</span>
        </span>
        <span className="spacer" />
        <button
          type="button"
          className="ghost small"
          disabled={disabled || Math.abs(offset - baseOffset) < 0.0005}
          onClick={() => {
            setOffset(baseOffset)
            if (Math.abs(baseOffset - take.offset) >= 0.0005) {
              onPatch(take.id, { offset: Number(baseOffset.toFixed(3)) })
            }
          }}
        >
          Сбросить сдвиг
        </button>
      </div>
    </div>
  )
}

export function TakeList({
  takes,
  disabled = false,
  onSetActive,
  onPatch,
  onDelete,
}: TakeListProps): JSX.Element {
  // Слайдер сдвига работает относительно значения, записанного при загрузке
  // дубля: базовый offset учитывает преролл и может быть большим по модулю.
  const baseOffsets = useRef(new Map<string, number>())
  const sorted = [...takes].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )
  for (const take of sorted) {
    if (!baseOffsets.current.has(take.id)) baseOffsets.current.set(take.id, take.offset)
  }

  if (sorted.length === 0) {
    return <p className="faint">Дублей пока нет — запишите первый.</p>
  }

  return (
    <div className="stack">
      {sorted.map((take, index) => (
        <TakeRow
          key={take.id}
          take={take}
          index={index}
          total={sorted.length}
          disabled={disabled}
          baseOffset={baseOffsets.current.get(take.id) ?? take.offset}
          onSetActive={onSetActive}
          onPatch={onPatch}
          onDelete={onDelete}
        />
      ))}
    </div>
  )
}
